import { LoadState } from "../../constants/enums";

/* ================================================================== *
 * Contact Lists (UI PRD Round 3 — Contact Lists module)
 *
 * Mirrors the emailSequences slice conventions:
 *  - plain interfaces + slice-state interface + initial state
 *  - typesafe-actions thunks hit the shared `api` instance under /sequence
 * ================================================================== */

export type ContactListStatus = "ACTIVE" | "INACTIVE";

export type ContactStatus =
  | "ACTIVE"
  | "INACTIVE"
  | "UNSUBSCRIBED"
  | "BOUNCED";

/** Row shape returned by get-sequence-contact-lists. */
export interface IContactListRow {
  list_code: number;
  list_name: string;
  description: string | null;
  total_contacts: number;
  active_contacts: number;
  status: ContactListStatus;
  created_by_name: string | null;
  create_ts: string;
}

/** Upsert payload for a contact list. `list_code` present only on edit. */
export interface IContactListUpsert {
  list_code?: number;
  list_name: string;
  description?: string | null;
  status: ContactListStatus;
}

/** Row shape returned by get-sequence-contact. */
export interface IContactRow {
  contact_list_code: number;
  list_code: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  event: string | null;
  reference: string | null;
  status: ContactStatus;
  notes: string | null;
  modified_by_name?: string | null;
  create_ts: string;
  insert_ts?: string;
}

/** Upsert payload for a single contact. */
export interface IContactUpsert {
  // list_code is required on create, optional on edit.
  list_code?: number;
  // contact_list_code identifies the row on edit / status change.
  contact_list_code?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string | null;
  event?: string | null;
  reference?: string | null;
  status?: ContactStatus;
  notes?: string | null;
}

/** One failed row in an upload report. */
export interface IContactUploadError {
  row: number;
  reason: string;
}

/** Response data from upload-sequence-contacts. */
export interface IContactUploadResult {
  total_rows: number;
  inserted: number;
  duplicates: number;
  errors: IContactUploadError[];
}

/* ------------------------------------------------------------------ *
 * Enrolment cron log (activation feedback, §5)
 * ------------------------------------------------------------------ */
export type CronLogStatus = "RUNNING" | "SUCCESS" | "FAILED" | "SKIPPED";

export interface IContactEnrollCronLog {
  cron_log_code?: string;
  status: CronLogStatus;
  rows_found: number;
  enrolled: number;
  message: string | null;
  summary: {
    not_enrolled?: Record<string, number>;
    [key: string]: any;
  } | null;
  insert_ts?: string;
}

/* ------------------------------------------------------------------ *
 * Slice state
 * ------------------------------------------------------------------ */
export interface ISequenceContactListsState {
  // Contact lists table
  lists: IContactListRow[];
  listsTotalRecords: number;
  listsLoading: LoadState;

  // Single list (detail header)
  list: IContactListRow | null;
  listLoading: LoadState;

  // Contacts inside a list
  contacts: IContactRow[];
  contactsTotalRecords: number;
  contactsLoading: LoadState;
}

export const initialSequenceContactListsState: ISequenceContactListsState = {
  lists: [],
  listsTotalRecords: 0,
  listsLoading: LoadState.NotLoaded,

  list: null,
  listLoading: LoadState.NotLoaded,

  contacts: [],
  contactsTotalRecords: 0,
  contactsLoading: LoadState.NotLoaded,
};

export const initialContactUpsert: IContactUpsert = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  event: "",
  reference: "",
  status: "ACTIVE",
  notes: "",
};

export const initialContactListUpsert: IContactListUpsert = {
  list_name: "",
  description: "",
  status: "ACTIVE",
};
